import type { PricingRuleResponse } from "@bw/shared/types/generated";
import { parseDecimal } from "@bw/shared/utils";

/**
 * The court-sheet price firewall.
 *
 * The backend `AvailabilitySlot` carries NO price (a slot is open-vs-not only),
 * so the per-hour cell estimate shown on the grid is derived CLIENT-SIDE from
 * the facility's pricing rules. A 1-hr slot estimate = base_price × 1.
 *
 * Crucially this is only honest when the facility has a SINGLE applicable
 * per-hour `court_reservation` rule. The backend `_select_pricing_rule` picks a
 * rule PER SLOT by time-of-day window, weekday, court scope, membership, and
 * priority — none of which a static day-grid can resolve client-side. So when
 * MORE THAN ONE per-hour court_reservation rule could apply to the selected day
 * (e.g. an off-peak window and a peak window), there is no single number that is
 * correct for every cell; painting one window's price onto the other's slots
 * fabricates a price the backend would never charge. In that case the estimate
 * is `null` → the cell renders "—". The real per-slot quote is Slice 3b.
 *
 * Two non-negotiables (plan.notes + .claude/rules/frontend/content-copy.md):
 *   1. NO raw arithmetic on the Decimal field — `base_price` is a Pydantic
 *      Decimal serialized as `number | string`; narrow it with `parseDecimal`
 *      (which rejects `"1e3"` and friends that would be a 1000× money bug).
 *   2. NO fabrication — no matching rule, >1 applicable rule, or an unparseable
 *      price → `null`, never an invented number.
 *
 * This is an ESTIMATE, not a binding quote. The binding combined quote is a 3b
 * concern (and currently has no player-callable endpoint — see the module
 * README "What this module does NOT own").
 */
function perHourCourtRules(
  rules: PricingRuleResponse[],
): PricingRuleResponse[] {
  return rules.filter(
    (r) => r.service === "court_reservation" && r.charge_unit === "per_hour",
  );
}

export function slotEstimate(rules: PricingRuleResponse[]): number | null {
  const matches = perHourCourtRules(rules);
  // Zero matches → unpriced. More than one → the per-slot price is selected by
  // the backend per time-window/weekday/membership and is NOT knowable here, so
  // we decline rather than fabricate one window's price for the whole day.
  if (matches.length !== 1) return null;

  // A 1-hr slot = base_price × 1. parseDecimal returns null for unparseable
  // garbage ("1e3", "", hex, etc.) — we surface that as null, never a number.
  return parseDecimal(matches[0].base_price);
}

/**
 * The ISO-4217 currency of the slot estimate, threaded to `formatMoney` so a
 * non-USD facility (e.g. CAD) is never formatted as bare US dollars (a
 * fabricated currency). Defaults to "USD" only when no single applicable rule
 * fixes one — mirroring `formatMoney`'s own default — so it is never read for a
 * "—" cell. Returns the rule's `currency` when exactly one rule applies.
 */
export function slotCurrency(rules: PricingRuleResponse[]): string {
  const matches = perHourCourtRules(rules);
  if (matches.length !== 1) return "USD";
  return matches[0].currency ?? "USD";
}
